import { buttonVariants, cn } from '@talentbabu/ui';
import {
  BookOpen,
  Building2,
  ChevronDown,
  CreditCard,
  Handshake,
  Info,
  LayoutDashboard,
  Mail,
  // MapPin,
  Menu,
  Newspaper,
  Phone,
  Search,
  UserRoundPlus,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { homePathFor, useAuthStore } from '../../app/auth-store';
import { Logo } from '../../components/Logo';
import { usePublicSettings } from '../../features/settings/public-settings';

interface MenuItem {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
}
type NavEntry = { label: string; to: string } | { label: string; items: MenuItem[] };

// function buildNav(city: string): NavEntry[] {
function buildNav(loggedIn: boolean): NavEntry[] {
  const nav: NavEntry[] = [
    { label: 'Home', to: '/' },
    // { label: 'About Us', to: '/about' },
    { label: 'Find Jobs', to: '/jobs' },
    // { label: 'Companies', to: '/companies' },
    {
      label: 'For Employers',
      items: [
        {
          to: '/employer/register',
          label: 'Create Profile',
          description: 'Get verified and publish openings',
          icon: Building2,
        },
        {
          to: '/services',
          label: 'Recruitment Services',
          description: 'Let our recruiters source for you',
          icon: Handshake,
        },
        {
          to: '/pricing',
          label: 'Plans & Pricing',
          description: 'Subscriptions, featured jobs, retainers',
          icon: CreditCard,
        },
        {
          to: '/contact',
          label: 'Talk to a Recruiter',
          description: 'Free consultation for your requirement',
          icon: Phone,
        },
      ],
    },
    {
      label: 'For Candidates',
      items: [
        {
          to: '/register',
          label: 'Create Profile',
          description: 'Apply faster and get TalentBabu Verified',
          icon: UserRoundPlus,
        },
        { to: '/jobs', label: 'Browse Jobs', description: 'Verified openings, updated daily', icon: Search },
        // {
        //   to: `/jobs?city=${encodeURIComponent(city)}`,
        //   label: `Jobs in ${city}`,
        //   description: 'Openings near you',
        //   icon: MapPin,
        // },
        { to: '/blog', label: 'Career Advice', description: 'Resume, interview and salary tips', icon: BookOpen },
      ],
    },
    {
      label: 'More',
      items: [
        { to: '/about', label: 'About Us', description: 'Our mission and team', icon: Info },
        { to: '/blog', label: 'Blogs', description: 'Hiring insights and news', icon: Newspaper },
        { to: '/contact', label: 'Contact', description: 'Get in touch', icon: Mail },
      ],
    },
  ];
  // "Create Profile" is only for logged-out visitors.
  if (!loggedIn) return nav;
  return nav.map((e) => ('items' in e ? { ...e, items: e.items.filter((i) => i.label !== 'Create Profile') } : e));
}

const isActivePath = (pathname: string, to: string) => {
  const path = to.split('?')[0];
  return path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(path + '/');
};

function Dropdown({ label, items, active }: { label: string; items: MenuItem[]; active: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // A click that follows a hover-open should not immediately close the menu again.
  const openedByHover = useRef(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useEffect(() => {
    const timer = closeTimer;
    return () => clearTimeout(timer.current);
  }, []);

  // Hover opens on pointer devices; a short delay lets the cursor cross the gap to the panel.
  const enter = () => {
    clearTimeout(closeTimer.current);
    if (!open) openedByHover.current = true;
    setOpen(true);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => {
      openedByHover.current = false;
      setOpen(false);
    }, 120);
  };

  return (
    <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          if (openedByHover.current) openedByHover.current = false;
          else setOpen((o) => !o);
        }}
        className={cn(
          'inline-flex cursor-pointer items-center gap-1 rounded-lg px-3 py-2 text-[15px] transition-colors',
          active || open ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900',
        )}
      >
        {label}
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 z-50 w-80 -translate-x-1/2 pt-3">
          <ul className="rounded-xl border border-slate-200 bg-white p-2 shadow-[var(--shadow-raised)]">
            {items.map(({ to, label: itemLabel, description, icon: Icon }) => (
              <li key={to + itemLabel}>
                <Link
                  to={to}
                  className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-slate-100"
                  onClick={() => setOpen(false)}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                    <Icon className="h-[18px] w-[18px]" aria-hidden />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{itemLabel}</span>
                    <span className="block text-[13px] text-slate-500">{description}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function SiteHeader() {
  const user = useAuthStore((s) => s.user);
  const { data: settings } = usePublicSettings();
  const profile = settings?.['company.profile'];
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  // const nav = buildNav(profile?.city ?? 'Raipur');
  const nav = buildNav(!!user);
  const linkActive = nav.some((e) => 'to' in e && isActivePath(location.pathname, e.to));
  const activeDropdown = linkActive
    ? undefined
    : nav.find((e) => 'items' in e && e.items.some((i) => isActivePath(location.pathname, i.to)))?.label;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The mobile menu covers the page: lock background scroll and close on Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <a
        href="#main"
        className="sr-only z-50 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-brand-700 shadow focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>

      <header
        className={cn(
          'sticky top-0 z-40 border-b backdrop-blur-md transition-colors',
          scrolled || open ? 'border-slate-200 bg-white/90' : 'border-transparent bg-transparent',
        )}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-6 px-4 sm:px-6">
          <Logo inverted imgClassName="h-11 sm:h-14" />

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Main">
            {nav.map((entry) =>
              'items' in entry ? (
                <Dropdown
                  key={entry.label}
                  label={entry.label}
                  items={entry.items}
                  active={activeDropdown === entry.label}
                />
              ) : (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'rounded-lg px-3 py-2 text-[15px] transition-colors',
                      isActive ? 'font-semibold text-slate-900' : 'text-slate-500 hover:text-slate-900',
                    )
                  }
                >
                  {entry.label}
                </NavLink>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2.5">
            {user ? (
              <Link
                to={homePathFor(user.role)}
                className={buttonVariants({ size: 'sm', className: 'site-glow hidden h-10 px-4 sm:inline-flex' })}
              >
                <LayoutDashboard className="h-4 w-4" /> My Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className={buttonVariants({
                    size: 'sm',
                    className: 'site-glow hidden h-10 px-4 sm:inline-flex',
                  })}
                >
                  Candidate Sign In
                </Link>
                <Link
                  to="/employer/login"
                  className={buttonVariants({
                    variant: 'outline',
                    size: 'sm',
                    className: 'hidden h-10 px-4 sm:inline-flex',
                  })}
                >
                  Employer/Job Post
                </Link>

                {/* <Link
                  to="/register"
                  className={buttonVariants({ size: 'sm', className: 'site-glow hidden h-10 px-5 sm:inline-flex' })}
                >
                  Get Started
                </Link> */}
                {/* <Link
                  to="/employer/register"
                  className={buttonVariants({
                    variant: 'outline',
                    size: 'sm',
                    className: 'hidden h-10 px-4 sm:inline-flex',
                  })}
                >
                  Employer Sign Up
                </Link> */}
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="-mr-1 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 lg:hidden"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="site-menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            id="site-menu"
            className="absolute inset-x-0 top-full h-[calc(100dvh-72px)] overflow-y-auto border-t border-slate-200 bg-[var(--site-bg)] lg:hidden"
          >
            <nav className="mx-auto max-w-7xl px-4 py-3 sm:px-6" aria-label="Mobile">
              <ul className="divide-y divide-slate-200">
                {nav.map((entry) =>
                  'items' in entry ? (
                    <li key={entry.label}>
                      <button
                        type="button"
                        aria-expanded={expanded === entry.label}
                        onClick={() => setExpanded(expanded === entry.label ? null : entry.label)}
                        className="flex w-full cursor-pointer items-center justify-between py-4 text-base font-medium text-slate-900"
                      >
                        {entry.label}
                        <ChevronDown
                          className={cn(
                            'h-5 w-5 text-slate-400 transition-transform',
                            expanded === entry.label && 'rotate-180',
                          )}
                          aria-hidden
                        />
                      </button>
                      {expanded === entry.label && (
                        <ul className="mb-3 space-y-1">
                          {entry.items.map(({ to, label, description, icon: Icon }) => (
                            <li key={to + label}>
                              <Link to={to} className="flex items-center gap-3 rounded-lg p-2.5 hover:bg-slate-100">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                                  <Icon className="h-[18px] w-[18px]" aria-hidden />
                                </span>
                                <span>
                                  <span className="block text-sm font-semibold text-slate-900">{label}</span>
                                  <span className="block text-[13px] text-slate-500">{description}</span>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <li key={entry.to}>
                      <NavLink
                        to={entry.to}
                        end={entry.to === '/'}
                        className={({ isActive }) =>
                          cn('block py-4 text-base font-medium', isActive ? 'text-brand-700' : 'text-slate-900')
                        }
                      >
                        {entry.label}
                      </NavLink>
                    </li>
                  ),
                )}
              </ul>
              <div className="mt-6 grid gap-3">
                {user ? (
                  <Link to={homePathFor(user.role)} className={buttonVariants({ size: 'lg', className: 'site-glow' })}>
                    <LayoutDashboard className="h-4 w-4" /> My Dashboard
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className={buttonVariants({ size: 'lg', className: 'site-glow' })}>
                      Get Started
                    </Link>
                    <Link to="/employer/login" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                      Employer Sign In
                    </Link>
                    <Link to="/login" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                      Sign In
                    </Link>
                  </>
                )}
              </div>
              {(profile?.phone || profile?.email) && (
                <div className="mt-6 space-y-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                  {profile?.phone && (
                    <a href={`tel:${profile.phone}`} className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-brand-600" aria-hidden /> {profile.phone}
                    </a>
                  )}
                  {profile?.email && (
                    <a href={`mailto:${profile.email}`} className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-brand-600" aria-hidden /> {profile.email}
                    </a>
                  )}
                </div>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
